import type { LibraryItemResponse } from "@poseref/shared";
import { useCallback, useEffect, useState } from "react";
import { useLibraryStore } from "../app/libraryStore.ts";
import { getReferenceImageUrl } from "../app/persistence/referenceImageDB.ts";
import { Button } from "./Button.tsx";
import { LibraryCard } from "./library/LibraryCard.tsx";
import { ModalShell } from "./ModalShell.tsx";
import { SearchField } from "./shared.tsx";
import {
  COLOR_TEXT,
  COLOR_TEXT_FAINT,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_SUBHEADING,
} from "./tokens.ts";

interface ReferenceLibraryModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: LibraryItemResponse) => void;
}

export function ReferenceLibraryModal({
  open,
  onClose,
  onSelect,
}: ReferenceLibraryModalProps) {
  const localReferences = useLibraryStore((s) => s.localReferences);
  const thumbnailUrls = useLibraryStore((s) => s.localReferenceThumbnailUrls);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Build items from local references
  const items: LibraryItemResponse[] = localReferences.map((ref) => ({
    id: `local:${ref.hash}`,
    folderId: null,
    type: "reference_image" as const,
    name: ref.name,
    thumbnailUrl: thumbnailUrls.get(ref.hash) ?? null,
    fileUrl: null,
    sourceFileUrl: null,
    sourceHash: ref.hash,
    hidden: false,
    fileSize: ref.size,
    metadata: null,
    createdAt: new Date(ref.createdAt).toISOString(),
    updatedAt: new Date(ref.createdAt).toISOString(),
  }));

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setSearch("");
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    const item = items.find((i) => i.id === selectedId);
    if (!item?.sourceHash) return;
    const imageUrl = await getReferenceImageUrl(item.sourceHash);
    if (imageUrl) {
      onSelect({ ...item, fileUrl: imageUrl });
    }
  }, [selectedId, items, onSelect]);

  // Only allow Enter to confirm when an item is selected
  const onConfirm = selectedId ? handleConfirm : undefined;

  const filtered = search
    ? items.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      width={560}
      style={{ maxHeight: "calc(100vh - 80px)" }}
      portal
    >
      {/* Header */}
      <span
        style={{
          fontSize: FONT_SIZE_SUBHEADING,
          fontWeight: 600,
          color: COLOR_TEXT,
        }}
      >
        Import from Library
      </span>

      {/* Search */}
      <SearchField
        value={search}
        onChange={setSearch}
        placeholder="Search references…"
      />

      {/* Grid */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {filtered.length === 0 ? (
          <div
            style={{
              padding: 20,
              textAlign: "center",
              fontSize: FONT_SIZE_BODY,
              color: COLOR_TEXT_FAINT,
            }}
          >
            {items.length === 0
              ? "No reference images stored on this device."
              : "No matches"}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 10,
              padding: "4px 0",
            }}
          >
            {filtered.map((item) => (
              <LibraryCard
                key={item.id}
                item={item}
                selected={selectedId === item.id}
                onSelect={() => setSelectedId(item.id)}
                onOpen={() => handleConfirm()}
                onRename={() => {}}
                onContextMenu={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: FONT_SIZE_SMALL, color: COLOR_TEXT_FAINT }}>
          {items.length} image{items.length !== 1 ? "s" : ""} on this device
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedId}
            variant="primary"
          >
            Import
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
